import { supabase } from "./supabase";
import {
  classifyFunctionsError,
  functionsFailureMessage,
  isIndeterminate,
} from "./supabaseFunctionsError";

/**
 * A failed edge-function call, carrying whether the failure proves the work was
 * skipped.
 *
 * DEV-838: quest creation failed in prod with functions-js's transport error
 * ("Failed to send a request to the Edge Function"), which by definition means
 * no answer came back -- the quest may or may not have been written. Every
 * failure used to arrive as a bare `Error`, identical in shape to a definite
 * 4xx refusal, so the UI could only say "it failed" and offer a retry that, in
 * the half of cases where the write DID land, creates a second quest.
 *
 * `supabaseFunctionsError.ts` already models this distinction for the callers
 * that unwrap `invoke` themselves; this carries it to the ones that go through
 * `callEdgeFunction`.
 */
export class EdgeFunctionError extends Error {
  /** True when the failure does NOT prove the function's work was skipped. */
  readonly indeterminate: boolean;

  constructor(message: string, indeterminate: boolean) {
    super(message);
    this.name = "EdgeFunctionError";
    this.indeterminate = indeterminate;
  }
}

/**
 * The ceiling every edge-function call is bounded by.
 *
 * This is a safety net against an infinite hang, NOT a latency policy. Some
 * functions are legitimately slow -- ai-decompose and translate-content run an
 * LLM -- so the default is generous enough that no existing caller changes
 * behaviour in practice. Paths where a stall is worse than a failure pass a
 * shorter `timeoutMs` of their own.
 */
export const DEFAULT_EDGE_TIMEOUT_MS = 60_000;

/**
 * A call that ran out of time rather than failing.
 *
 * functions-js reports an aborted fetch as a plain FunctionsFetchError, which
 * is indistinguishable from being offline. Naming the case lets callers and
 * Sentry tell "the server never answered" apart from "the browser could not
 * reach it" -- a distinction that decides whether retrying is worth anything.
 *
 * Indeterminate by construction (DEV-838): we abort the fetch from this side,
 * which says nothing about whether the server went on to do the work.
 */
export class EdgeFunctionTimeoutError extends EdgeFunctionError {
  constructor(
    readonly fnName: string,
    readonly timeoutMs: number,
  ) {
    super(`Edge Function "${fnName}" timed out after ${timeoutMs}ms`, true);
    this.name = "EdgeFunctionTimeoutError";
  }
}

interface EdgeFunctionOptions {
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  /** Override the default ceiling. See DEFAULT_EDGE_TIMEOUT_MS. */
  timeoutMs?: number;
}

/** True when functions-js wrapped our own timeout's AbortError. */
function isAbort(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("context" in error)) return false;
  const { context } = error as { context?: unknown };
  return (
    typeof context === "object" &&
    context !== null &&
    (context as { name?: unknown }).name === "AbortError"
  );
}

/**
 * Call a Supabase Edge Function.
 *
 * The Supabase client automatically sets the Authorization header to the
 * exchanged Supabase JWT (via the `accessToken` config). This passes the
 * gateway's JWT verification. The edge function reads the user ID from
 * the JWT's `sub` claim.
 *
 * Every call is bounded: `invoke` aborts the fetch at `timeoutMs`, so a
 * function that never answers rejects instead of leaving the caller's promise
 * pending forever. That mattered for the `profiles` role fetch, whose
 * completion gates `profileSynced` -- a hang there left the app believing the
 * signed-in user's role was still unknown, indefinitely.
 *
 * @param fnName   – Edge Function name (e.g. "ai-decompose", "quests")
 * @param options  – Optional body, extra headers and timeout override
 * @returns Parsed JSON response from the Edge Function
 * @throws EdgeFunctionTimeoutError when the call exceeds its deadline
 */
export async function callEdgeFunction<T = unknown>(
  fnName: string,
  options?: EdgeFunctionOptions,
): Promise<T> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_EDGE_TIMEOUT_MS;

  const { data, error } = await supabase.functions.invoke(fnName, {
    body: options?.body ?? {},
    headers: options?.headers,
    timeout: timeoutMs,
  });

  if (error) {
    if (isAbort(error)) throw new EdgeFunctionTimeoutError(fnName, timeoutMs);

    // Classified centrally rather than unwrapped here, so this path reads the
    // failure exactly the way the hand-rolled call sites already do -- keeping
    // the function's own error code (e.g. "rate_limited"), which callers
    // pattern-match on, and naming the status when a non-2xx has no body.
    const failure = await classifyFunctionsError(error);
    const message = functionsFailureMessage(failure) || `Edge Function "${fnName}" failed`;
    throw new EdgeFunctionError(message, isIndeterminate(failure));
  }

  return data as T;
}
